import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

export interface SolarData {
  pvYieldW:           number;
  batteryDischargeW:  number;
  batteryPercent:     number;
  gridConsumptionW:   number;
  totalConsumptionW:  number;
  batteryCapacityWh:  number;
  consumptionL1W:     number;
  consumptionL2W:     number;
  consumptionL3W:     number;
}

export interface ThermometerReading {
  name:     string;
  deviceId: string;
  tempC:    number;
  humidity: number | null;
}

export interface TempDataPoint {
  ts:       number;
  tempC:    number;
  humidity: number | null;
}

export type DayFaultReason = 'WIFI_OUTAGE' | 'TIGO_OUTAGE' | 'DATA_MISSING' | 'DEYE_FREEZE' | 'NO_YIELD';

export interface DayStat {
  date:             string;
  generationKwh:    number;
  consumptionKwh:   number;
  gridImportKwh:    number;
  gridExportKwh:    number;
  autarkiePercent:  number;
  faultReason?:     DayFaultReason | null;
  faultComment?:    string | null;
  sunriseUtc?:      string | null;
  sunsetUtc?:       string | null;
}

export interface HourlyDataPoint {
  ts:             number;  // epoch ms
  generationKwh:  number;
  consumptionKwh: number;
  gridImportKwh:  number;
  gridExportKwh:  number;
}

export interface WastePickup {
  date: string;   // ISO: YYYY-MM-DD
  type: string;   // e.g. PPK, Bio, GT, RM, A, SoH, TB
  name: string;   // German name from calendar
}

export interface SolarHistoryPoint {
  ts:               number;
  pvYieldW:         number;
  batteryDischargeW: number;
  gridConsumptionW: number;
  totalConsumptionW: number;
  batteryPercent:   number;
  consumptionL1W:   number;
  consumptionL2W:   number;
  consumptionL3W:   number;
}

export interface FroelingError {
  code:      string;
  message:   string;
  severity:  string;
  timestamp: string;
}

export interface FroelingHeatingCircuit {
  name:             string;
  flowTempActualC:  number;
  flowTempTargetC:  number;
  pumpState:        string;
}

export interface FroelingData {
  boilerTempC:          number;
  exhaustTempC:         number;
  boilerState:          string;
  boilerModulationPct:  number;
  returnTempC:          number;
  oxygenPct:            number;
  draftFanPct:          number;
  operatingHours:       number;
  bufferTopTempC:       number;
  bufferBottomTempC:    number;
  bufferChargePct:      number;
  bufferTempC:          number;
  outdoorTempC:         number;
  hotWaterTempC:        number;
  flowTempActualC:      number;
  flowTempTargetC:      number;
  pumpState:            string;
  pelletLevelPct:       number;
  pelletConsumptionKg:  number;
  solarThermalTempC:    number;
  solarThermalPumpPct:  number;
  circulationPumpPct:   number;
  heatingCircuits:      FroelingHeatingCircuit[];
  errors:               FroelingError[];
}

export interface PelletDayStat {
  date:           string;
  consumptionKg:  number;
  levelPct:       number;
}

export interface FroelingHistoryPoint {
  ts:                   number;
  boilerTempC:          number;
  exhaustTempC:         number;
  boilerModulationPct:  number;
  bufferTopTempC:       number;
  bufferBottomTempC:    number;
  bufferChargePct:      number;
  hotWaterTempC:        number;
  flowTempActualC:      number;
  outdoorTempC:         number;
  solarThermalTempC:    number;
  pelletConsumptionKg:  number;
  circulationPumpPct:   number;
  heatingCircuits:      { name: string; flowTempActualC: number; pumpState?: string }[];
}

export interface UnifiDevice {
  name:       string;
  type:       string;    // uap, usw, ugw, udm, uxg
  model:      string;
  shortname:  string;
  online:     boolean;
  uptime:     number;    // seconds
  ip:         string;
  numClients: number;    // -1 if not applicable
  rxBps:      number;
  txBps:      number;
}

export interface UnifiClient {
  hostname: string;
  ip:       string;
  isWifi:   boolean;
  signal:   number;    // dBm (0 for wired)
  rxBytes:  number;
  txBytes:  number;
  rxBps:    number;
  txBps:    number;
}

export interface UnifiSnapshot {
  wanUp:        boolean;
  wanRxBps:     number;
  wanTxBps:     number;
  wanUptime:    number;   // seconds
  wanLatency:   number;   // ms, -1 = unknown
  wanIp:        string | null;
  wanIsp:       string | null;
  totalClients: number;
  lanClients:   number;
  wlanClients:  number;
  devices:      UnifiDevice[];
  topClients:   UnifiClient[];
}

export interface ZisterneData {
  levelPct:  number;
  depthCm:   number;
  battery:   number;
}

export interface ZisterneHistoryPoint {
  ts:       number;
  levelPct: number;
  depthCm:  number;
}

@Injectable({ providedIn: 'root' })
export class VersionService implements OnDestroy {

  readonly newVersion$          = new Subject<string>();
  readonly solar$               = new Subject<SolarData>();
  readonly solarHistory$        = new Subject<SolarHistoryPoint[]>();
  readonly thermometers$        = new Subject<ThermometerReading[]>();
  readonly thermometerHistory$  = new Subject<{[deviceId: string]: TempDataPoint[]}>();
  readonly stats$               = new Subject<DayStat[]>();
  readonly waste$               = new Subject<WastePickup[]>();
  readonly unifi$               = new Subject<UnifiSnapshot>();
  readonly froeling$            = new Subject<FroelingData>();
  readonly froelingHistory$     = new Subject<FroelingHistoryPoint[]>();
  readonly pelletStats$         = new Subject<PelletDayStat[]>();
  readonly zisterne$            = new Subject<ZisterneData>();
  readonly zisterneHistory$     = new Subject<ZisterneHistoryPoint[]>();

  connectedClients = 0;
  sunrise: string | null = null;
  sunset:  string | null = null;

  private knownCommit: string | null = null;
  private worker!: Worker;

  frontendCommit    = '';
  backendCommit     = '';
  connected         = true;
  wsConnectedOnce   = false;

  // Centralized new-version reload state, shared by the dashboard and Claudia overlays.
  reloadPending     = false;
  reloadCountdown   = 5;
  private reloadTimer: any = null;
  private started   = false;

  constructor(private zone: NgZone) {}

  connect(frontendCommit: string): void {
    if (this.started) return;            // idempotent: one worker/WS per page load
    this.started = true;
    this.frontendCommit = frontendCommit;

    this.worker = new Worker(new URL('./version.worker', import.meta.url), { type: 'module' });

    this.worker.onmessage = ({ data }) => {
      this.zone.run(() => {
        if      (data.kind === 'data')         this.handleMessage(data.payload);
        else if (data.kind === 'connected')    { this.connected = true; this.wsConnectedOnce = true; }
        else if (data.kind === 'disconnected') this.connected = false;
        else if (data.kind === 'error')        this.connected = false;
      });
    };
  }

  private handleMessage(msg: any): void {
    switch (msg.type) {
      case 'hello':
        this.backendCommit = msg.commit;
        // First hello on page load: record, never reload. A different commit on a
        // later hello means the backend restarted → show the update overlay.
        if (!this.knownCommit) this.knownCommit = msg.commit;
        else if (msg.commit !== this.knownCommit) { this.newVersion$.next(msg.commit); this.triggerReload(); }
        break;
      case 'update':          this.newVersion$.next(''); this.triggerReload(); break;  // backend about to restart
      case 'solar':           this.solar$.next(msg as SolarData); break;
      case 'solarHistory':    this.solarHistory$.next(msg.points as SolarHistoryPoint[]); break;
      case 'thermometers':
        this.thermometers$.next(msg.sensors as ThermometerReading[]);
        if (msg.history) this.thermometerHistory$.next(msg.history);
        break;
      case 'stats':           this.stats$.next(msg.days as DayStat[]); break;
      case 'clients':         this.connectedClients = msg.count; break;
      case 'sunrise':         this.sunrise = msg.sunrise; this.sunset = msg.sunset; break;
      case 'waste':           this.waste$.next(msg.pickups as WastePickup[]); break;
      case 'unifi':           this.unifi$.next(msg.data as UnifiSnapshot); break;
      case 'froeling':        this.froeling$.next(msg.data as FroelingData); break;
      case 'froelingHistory': this.froelingHistory$.next(msg.points as FroelingHistoryPoint[]); break;
      case 'pelletStats':     this.pelletStats$.next(msg.days as PelletDayStat[]); break;
      case 'zisterne':        this.zisterne$.next(msg.data as ZisterneData); break;
      case 'zisterneHistory': this.zisterneHistory$.next(msg.points as ZisterneHistoryPoint[]); break;
    }
  }

  /** Show the reload overlay and reload after a short countdown (once). */
  private triggerReload(): void {
    if (this.reloadPending) return;
    this.reloadPending = true;
    this.reloadCountdown = 5;
    this.reloadTimer = setInterval(() => {
      this.reloadCountdown--;
      if (this.reloadCountdown <= 0) { clearInterval(this.reloadTimer); location.reload(); }
    }, 1000);
  }

  ngOnDestroy(): void {
    this.worker?.terminate();
    if (this.reloadTimer) clearInterval(this.reloadTimer);
  }
}
